import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ApiError } from '../api/types';
import { ToastProvider } from '../components/ui/Toast';
import { TooltipProvider } from '../components/ui/Menu';
import { SessionProvider } from './session';
import { SiteProvider } from './site';
import { ThemeProvider } from './theme';

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Client errors will not fix themselves; only retry transport faults.
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider delayDuration={300} skipDelayDuration={200}>
          <ToastProvider>
            <SessionProvider>
              <SiteProvider>{children}</SiteProvider>
            </SessionProvider>
          </ToastProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
